import {
  Box,
  Center,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  type AllocatedTimeBaseProps,
  AllocatedTimeCalendarDay,
} from "@src/components/ui-kit/allocated-time-indicator/calendar-day";
import { Icon } from "@src/components/ui-kit/Icon";
import { LoadingOverlay } from "@src/components/ui-kit/LoadingOverlay/LoadingOverlay";
import { toApiDate } from "@src/utils/dates";
import { useStore } from "@src/utils/hooks";
import {
  Calendar,
  CalendarControls,
  CalendarDays,
  CalendarMonth,
  CalendarMonthName,
  CalendarMonths,
  CalendarNextButton,
  CalendarPrevButton,
  type CalendarValues,
  CalendarWeek,
} from "@uselessdev/datepicker";
import { addMonths, endOfMonth, startOfMonth, subMonths } from "date-fns";
import { action, computed, makeObservable, observable } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useEffect } from "react";
import type { UsersAllocatedTimeStore } from "./store";

interface Props extends AllocatedTimeBaseProps {
  store: UsersAllocatedTimeStore;
  userId?: string;
}
export const AllocatedTimeIndicator = observer(function AllocatedTimeIndicator({
  store,
  userId,
  from,
  to,
  fromTime,
  toTime,
  reccurentDays,
  variant = "allocation",
}: Props) {
  const { workspaceStore } = useStore();
  const state = useLocalObservable(
    () => new State(store, userId, from ?? new Date()),
  );

  useEffect(() => {
    state.setUserId(userId);
  }, [userId]);

  return (
    <Popover
      isLazy
      isOpen={state.hasConflict ? undefined : false}
      onOpen={() => state.fetchMonthData(state.month)}
      trigger="hover"
    >
      <PopoverTrigger>
        <Center h="9">
          {store.isLoadingConflicts ? (
            <>
              <Spinner size="sm" />
            </>
          ) : state.hasConflict ? (
            <Icon
              name="calendar-cross-01"
              color="red.500"
              aria-label={t`Time-offs`}
            />
          ) : (
            <Box w="4" h="0.5" bg="gray.400" />
          )}
        </Center>
      </PopoverTrigger>

      <PopoverContent p="0" bg="transparent" border="none" shadow="none">
        <PopoverBody>
          <Calendar
            weekStartsOn={workspaceStore.settings?.startOfWeekNumber}
            value={state.calendarValue}
            onSelectDate={() => {}}
          >
            <CalendarControls>
              <CalendarPrevButton
                as={(props) => (
                  <IconButton
                    {...props}
                    zIndex="32"
                    aria-label={t`Prev month`}
                    icon={<Icon name="arrow-narrow-left" boxSize="4" />}
                    onClick={state.handleSetPrevMonth}
                  />
                )}
              />
              <CalendarNextButton
                as={(props) => (
                  <IconButton
                    {...props}
                    zIndex="32"
                    aria-label={t`Next month`}
                    icon={<Icon name="arrow-narrow-right" boxSize="4" />}
                    onClick={state.handleSetNextMonth}
                  />
                )}
              />
            </CalendarControls>

            <CalendarMonths bg="white" rounded="md" overflow="hidden">
              <CalendarMonth>
                <CalendarMonthName />
                <CalendarWeek />
                <LoadingOverlay
                  isLoading={store.isLoadingAllocatedTime}
                  position="absolute"
                >
                  <CalendarDays>
                    <AllocatedTimeCalendarDay
                      userAllocatedTime={state.userData}
                      variant={variant}
                      from={from}
                      to={to}
                      fromTime={fromTime}
                      toTime={toTime}
                      reccurentDays={reccurentDays}
                    />
                  </CalendarDays>
                </LoadingOverlay>
              </CalendarMonth>
            </CalendarMonths>
          </Calendar>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
});

class State {
  @observable.ref
  userId: string | undefined;

  @observable.ref
  month: Date;

  constructor(
    private userAllocatedTimeStore: UsersAllocatedTimeStore,
    userId: string | undefined,
    month: Date | undefined,
  ) {
    makeObservable(this);
    this.userId = userId;
    this.month = month ?? new Date();
  }

  @action.bound
  setUserId(userId: string | undefined) {
    this.userId = userId;
  }

  @computed
  get userData() {
    return this.userAllocatedTimeStore.getUserData(this.userId);
  }

  @computed
  get hasConflict() {
    return this.userAllocatedTimeStore.getUserHasConflicts(this.userId);
  }

  @computed
  get calendarValue(): CalendarValues {
    return { start: startOfMonth(this.month), end: endOfMonth(this.month) };
  }

  @action.bound
  setMonth(month: Date) {
    this.month = month;
  }

  @action.bound
  handleSetPrevMonth() {
    const prevMonth = subMonths(this.month, 1);
    this.setMonth(prevMonth);
    this.fetchMonthData(prevMonth);
  }

  @action.bound
  handleSetNextMonth() {
    const nextMonth = addMonths(this.month, 1);
    this.setMonth(nextMonth);
    this.fetchMonthData(nextMonth);
  }

  @action.bound
  fetchMonthData(date: Date) {
    if (!this.userId) return;

    this.userAllocatedTimeStore.fetchAllocatedTime({
      from: toApiDate(startOfMonth(date)),
      to: toApiDate(endOfMonth(date)),
      user_ids: this.userId,
    });
  }
}
